/**
 * Skipper upload page — /regatta/:slug/upload
 * Authenticated participant uploads tracks for their boat.
 * Requires auth + an Entry in the regatta.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { BrandLogo } from '@/components/brand/BrandLogo';
import type { Regatta, Entry, RawTrack, GetRegattaResponse } from '@regatta-replay/types';
import { formatSailNumber } from '@regatta-replay/types';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { UploadDropzone } from '@/components/upload/UploadDropzone';
import { SkipperUploadTable } from '@/components/upload/SkipperUploadTable';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type PageState = 'loading' | 'ready' | 'not-participant' | 'error';

export function SkipperUploadPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuthStore();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [regatta, setRegatta] = useState<Regatta | null>(null);
  const [entry, setEntry] = useState<Entry | null>(null);
  const [uploads, setUploads] = useState<RawTrack[]>([]);
  const [raceDates, setRaceDates] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  // Auth guard
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Load data
  useEffect(() => {
    if (!slug || !isAuthenticated) return;

    let cancelled = false;

    async function loadData() {
      try {
        // Load regatta by slug
        const regattaData = await api.get<GetRegattaResponse>(
          `/regattas/by-slug/${slug}`
        );
        if (cancelled) return;

        setRegatta(regattaData.regatta);

        // Extract race dates from summaries
        const dates = [
          ...new Set(regattaData.raceSummaries.map((r) => r.date)),
        ];
        setRaceDates(dates);

        // Load user's entry
        const entryData = await api.get<{ entry: Entry }>(
          `/regattas/${regattaData.regatta.regattaId}/my-entry`
        );
        if (cancelled) return;

        if (!entryData.entry) {
          setPageState('not-participant');
          return;
        }

        setEntry(entryData.entry);

        // Load user's uploads
        const uploadData = await api.get<{ items: RawTrack[] }>(
          `/regattas/${regattaData.regatta.regattaId}/my-uploads`
        );
        if (cancelled) return;

        setUploads(uploadData.items);
        setPageState('ready');
      } catch {
        if (cancelled) return;
        setPageState('error');
        setErrorMessage('Failed to load page data.');
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, [slug, isAuthenticated]);

  const handleUploadComplete = useCallback(() => {
    // Reload uploads after a new file completes
    if (!regatta) return;
    api
      .get<{ items: RawTrack[] }>(`/regattas/${regatta.regattaId}/my-uploads`)
      .then((data) => setUploads(data.items))
      .catch(() => {});
  }, [regatta]);

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="h-14 bg-navy-900 text-white flex items-center px-6">
        <Link to={`/regatta/${slug}`} className="mr-auto">
          <BrandLogo variant="dark" />
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-1 px-3 py-1.5 rounded text-sm hover:bg-white/10 transition-colors">
            {user?.name || 'User'}
            <ChevronDown className="h-3.5 w-3.5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                logout();
                navigate('/login');
              }}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-3xl px-4 py-8">
        {/* Loading */}
        {pageState === 'loading' && (
          <div className="space-y-4" data-testid="upload-page-loading">
            <Skeleton className="h-8 w-72" />
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        )}

        {/* Not participant */}
        {pageState === 'not-participant' && (
          <div className="text-center py-16" data-testid="not-participant">
            <h1 className="text-xl font-semibold text-navy-900">
              Not a participant
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              You don't have an entry in this regatta.
            </p>
            <Link
              to={`/regatta/${slug}`}
              className="mt-4 inline-block text-sm font-medium text-brand-ink hover:underline"
            >
              Back to Regatta
            </Link>
          </div>
        )}

        {/* Error */}
        {pageState === 'error' && (
          <div className="text-center py-16" data-testid="upload-page-error">
            <h1 className="text-xl font-semibold text-navy-900">Error</h1>
            <p className="mt-2 text-sm text-muted-foreground">{errorMessage}</p>
          </div>
        )}

        {/* Ready */}
        {pageState === 'ready' && regatta && entry && (
          <div className="space-y-6" data-testid="upload-page-content">
            <div>
              <h1 className="text-2xl font-semibold text-navy-900">
                Upload Tracks — {regatta.name}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {entry.boatName} ({formatSailNumber(entry.sailNumber)})
              </p>
            </div>

            <UploadDropzone
              regattaId={regatta.regattaId}
              onUploadComplete={handleUploadComplete}
            />

            <SkipperUploadTable
              uploads={uploads}
              raceDates={raceDates}
            />

            <Link
              to={`/regatta/${slug}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-brand-ink hover:underline"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Regatta
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
